/*
 * DateUtils.ts
 * ===========
 * Helper functions relating to date and time.
 */
import spacetime from "spacetime";
import ProtoUtils from "./ProtoUtils";
import NLP from "./NLP";
export default class DateUtils {
    static DayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    static MonthNames = ["January", "February", "March", "April", "May", "June", "July", "August",
        "September", "October", "November", "December"];
    static defaultTimezone = "America/Los_Angeles";
    static utcTimezone = "UTC";
    static TimesOfDay = {
        morning: {
            name: "morning",
            prev: "night",
            next: "afternoon",
            match: (stime) => { return stime.hour() >= 5 && stime.hour() < 12; },
            getNextTime(stime) {
                if (this.match(stime))
                    return stime;
                let stimeNext = spacetime(stime, stime.tz).time("5:00");
                if (stime.hour() >= 12) {
                    stimeNext = stimeNext.add(1, "day");
                }
                return stimeNext;
            }
        },
        afternoon: {
            name: "afternoon",
            prev: "morning",
            next: "evening",
            match: (stime) => { return stime.hour() >= 12 && stime.hour() < 17; },
            getNextTime(stime) {
                if (this.match(stime))
                    return stime;
                let stimeNext = spacetime(stime, stime.tz).time("12:00");
                if (stime.hour() >= 17) {
                    stimeNext = stimeNext.add(1, "day");
                }
                return stimeNext;
            }
        },
        evening: {
            name: "evening",
            prev: "afternoon",
            next: "night",
            match: (stime) => { return stime.hour() >= 17 && stime.hour() < 22; },
            getNextTime(stime) {
                if (this.match(stime))
                    return stime;
                let stimeNext = spacetime(stime, stime.tz).time("17:00");
                if (stime.hour() >= 17) {
                    stimeNext = stimeNext.add(1, "day");
                }
                return stimeNext;
            }
        },
        night: {
            name: "night",
            prev: "evening",
            next: "morning",
            match: (stime) => {
                return stime.hour() >= 22 && stime.hour() < 24 || stime.hour() >= 0 && stime.hour() < 5;
            },
            getNextTime(stime) {
                if (this.match(stime))
                    return stime;
                return spacetime(stime, stime.tz).time("22:00");
            }
        },
    };
    /**
     * Returns the local time zone for the system on which this code is running.
     */
    static localTimezone() {
        return spacetime.now().tz;
    }
    // Checks if the two time points are on the same day, according to the timezone of the ref time
    static matchDay(stimeRef, stimeTest) {
        if (!stimeRef && !stimeTest)
            return true;
        if (!stimeRef || !stimeTest)
            return false;
        const st1 = stimeRef;
        const st2 = spacetime(stimeTest.epoch, stimeRef.tz);
        return st1.date() === st2.date() &&
            st1.month() === st2.month() &&
            st1.year() === st2.year();
    }
    // Gets the time, e.g. 4:52 pm
    static getTimeString(stime) {
        if (!stime)
            return "";
        return stime.format("{hour}:{minute-pad} {ampm}");
    }
    static getTimeOfDay(stime) {
        for (const timeOfDay of Object.values(DateUtils.TimesOfDay)) {
            if (timeOfDay.match(stime)) {
                return timeOfDay;
            }
        }
        return undefined;
    }
    static getDateString(date) {
        return DateUtils.DayNames[date.getDay()] + " " + date.toLocaleDateString();
    }
    /**
     * Convenience function for providing human readable date and time.
     * Used in the location statistics visualizer.
     */
    static getDateTimeString(date) {
        return this.getDateString(date) + " " + this.getTimeString(spacetime(date));
    }
    /**
     * This provides a human readable string from a duration in minutes.
     * Used in the location statistics visualizer.
     */
    static getDurationString(duration_minutes) {
        const minutesInHour = 60;
        const minutesInDay = 1440;
        if (duration_minutes < minutesInHour) {
            return `${Math.floor(duration_minutes)} minutes`;
        }
        else if (duration_minutes < minutesInDay) {
            const hours = Math.floor(duration_minutes / minutesInHour);
            const minutes = Math.floor(duration_minutes % minutesInHour);
            return `${hours} hour${hours !== 1 ? "s" : ""}${minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? "s" : ""}` : ""}`;
        }
        else {
            const days = duration_minutes / minutesInDay;
            return `${days.toFixed(2)} days`;
        }
    }
    static getDayOfWeek(stime) {
        if (!stime) {
            stime = spacetime.now();
        }
        const dayName = stime.dayName();
        return NLP.capitalizeFirstLetter(dayName);
    }
    /**
     * This is used to reason about a person's semantic day, which is set to begin/end at 5 am.
     * Events before 5 am are considered part of the previous "day" for a user.
     */
    static eventOccursOnDay(event, stimeDay) {
        if (!event.startDate || !event.endDate)
            return false;
        const stEventStart = ProtoUtils.toSpacetime(event.startDate, stimeDay.tz);
        const stEventEnd = ProtoUtils.toSpacetime(event.endDate, stimeDay.tz);
        let stDayStart, stDayEnd;
        if (DateUtils.isStartOfDay(stimeDay)) {
            stDayStart = stimeDay.time("5am");
            stDayEnd = stimeDay.add(1, "day").time("4:59am");
        }
        else {
            if (stimeDay.hour() < 5) {
                stDayStart = stimeDay.subtract(1, "day").time("5am");
                stDayEnd = stimeDay.time("4:59am");
            }
            else {
                stDayStart = stimeDay.time("5am");
                stDayEnd = stimeDay.add(1, "day").time("4:59am");
            }
        }
        return stEventStart.isAfter(stDayStart) && stEventStart.isBefore(stDayEnd) ||
            stEventEnd.isAfter(stDayStart) && stEventEnd.isBefore(stDayEnd);
    }
    /**
     * Used to reason about a user's semantic day. To check the date only, use matchDay().
     */
    static occursOnDay(st, stimeDay) {
        let stDayStart, stDayEnd;
        if (DateUtils.isStartOfDay(stimeDay)) {
            stDayStart = stimeDay.time("5am");
            stDayEnd = stimeDay.add(1, "day").time("4:59am");
        }
        else {
            if (stimeDay.hour() < 5) {
                stDayStart = stimeDay.subtract(1, "day").time("5am");
                stDayEnd = stimeDay.time("4:59am");
            }
            else {
                stDayStart = stimeDay.time("5am");
                stDayEnd = stimeDay.add(1, "day").time("4:59am");
            }
        }
        return st.isAfter(stDayStart) && st.isBefore(stDayEnd);
    }
    /**
     * Returns whether the provided date is the earliest time of that day. Usually this means that a
     * date has been matched and no time has been provided.
     */
    static isStartOfDay(st) {
        const startOfDay = st.startOf("day");
        return st.diff(startOfDay, "milliseconds") === 0;
    }
    static getPeriodString(seconds) {
        const hours = seconds / 60 / 60;
        const days = hours / 24;
        const weeks = days / 7;
        const years = days / 365;
        const months = years * 12;
        const findClosest = (arr, ref) => arr.reduce((acc, val, idx) => {
            const isCloser = Math.abs(val - ref) < Math.abs(acc.value - ref);
            return isCloser ? { index: idx, value: val } : acc;
        }, { index: -1, value: Infinity });
        const units = ["hour", "day", "week", "month", "year"];
        const numbers = [hours, days, weeks, months, years];
        const referenceValue = 6;
        const closest = findClosest(numbers, referenceValue);
        const index = closest.index;
        const unit = units[index];
        const value = +closest.value.toFixed(1);
        let string = `${value} ${unit}`;
        if (value !== 1) {
            string += "s";
        }
        return string;
    }
}
class DateRange {
    start;
    end;
    static afterNowByDurationMins(durationMins) {
        const dateRange = new DateRange();
        dateRange.start = new Date();
        dateRange.end = new Date();
        dateRange.end.setMinutes(dateRange.start.getMinutes() + durationMins);
        return dateRange;
    }
    static beforeAndAfterNowByDurationMins(pastDurationMins, futureDurationMins) {
        const dateRange = new DateRange();
        dateRange.start = new Date();
        dateRange.start.setMinutes(dateRange.start.getMinutes() - pastDurationMins);
        dateRange.end = new Date();
        dateRange.end.setMinutes(dateRange.end.getMinutes() + futureDurationMins);
        return dateRange;
    }
}
export { DateRange };
